export const AUTH_STORAGE_KEYS = {
  partnerId: "auth_partner_id",
  challengeId: "auth_challenge_id",
  setupToken: "auth_setup_token",
  tempToken: "auth_temp_token",
  accessToken: "auth_access_token",
  refreshToken: "auth_refresh_token",
  hasSession: "has3cxSession",
  flowStep: "auth_flow_step",
};

export function saveAuthValue(key, value) {
  localStorage.setItem(key, value);
}

export function getAuthValue(key) {
  return localStorage.getItem(key);
}

export function removeAuthValue(key) {
  localStorage.removeItem(key);
}

export function clearAuthFlow() {
  removeAuthValue(AUTH_STORAGE_KEYS.partnerId);
  removeAuthValue(AUTH_STORAGE_KEYS.challengeId);
  removeAuthValue(AUTH_STORAGE_KEYS.setupToken);
  removeAuthValue(AUTH_STORAGE_KEYS.tempToken);
  removeAuthValue(AUTH_STORAGE_KEYS.flowStep);
}

export function setAuthFlowStep(step) {
  saveAuthValue(AUTH_STORAGE_KEYS.flowStep, step);
}

export function getAuthFlowStep() {
  return getAuthValue(AUTH_STORAGE_KEYS.flowStep);
}

const API_BASE = process.env.REACT_APP_API_SERVER;

// ─── Helpers ──────────────────────────────────────────────────────────────────
async function post(path, body, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: path.startsWith("/wp-json/") ? "include" : undefined,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...options,
  });

  const rawText = await res.text().catch(() => "");
  const data = (() => {
    if (!rawText) return {};
    try {
      return JSON.parse(rawText);
    } catch {
      return { message: rawText };
    }
  })();

  if (!res.ok) {
    const message =
      data?.error ||
      data?.message ||
      data?.detail ||
      `Request failed (${res.status})`;

    // Friendly auth/start message
    if (path.endsWith("/auth/start") && (res.status === 404 || res.status === 400)) {
      throw new Error("Partner ID not found");
    }

    throw new Error(message);
  }

  return data;
}

// ─── 1. POST /api/auth/start ──────────────────────────────────────────────────
// Response: { status: "email_verification_required", challengeId }
//        or { status: "totp_required", tempToken }
export async function authStart(partner) {
  const data = await post("/wp-json/threecx/v1/auth/start", { partner });
  saveAuthValue(AUTH_STORAGE_KEYS.partnerId, partner);
  if (data.challengeId)
    saveAuthValue(AUTH_STORAGE_KEYS.challengeId, data.challengeId);
  if (data.tempToken)
    saveAuthValue(AUTH_STORAGE_KEYS.tempToken, data.tempToken);
  return data;
}

// ─── 2. POST /api/auth/verify-email ──────────────────────────────────────────
// Response: { status: "totp_setup_required", setupToken }
//        or { status: "totp_required", tempToken }
export async function authVerifyEmail(challengeId, otp) {
  const data = await post("/wp-json/threecx/v1/auth/verify-email", {
    challengeId,
    otp,
  });
  if (data.setupToken)
    saveAuthValue(AUTH_STORAGE_KEYS.setupToken, data.setupToken);
  if (data.tempToken)
    saveAuthValue(AUTH_STORAGE_KEYS.tempToken, data.tempToken);
  return data;
}

// ─── 3. POST /api/auth/2fa/setup ─────────────────────────────────────────────
// Response: { status: "setup_started", qrCode: "data:image/png;base64,...", manualCode }
export async function authTotpSetup(setupToken) {
  const data = await post("/wp-json/threecx/v1/auth/2fa/setup", { setupToken });

  if (data?.qrCode && typeof data.qrCode === "string") {
    const qr = data.qrCode.trim();
    const isDataUrl = qr.startsWith("data:");
    const isHttpUrl = /^https?:\/\//i.test(qr);
    if (!isDataUrl && !isHttpUrl) {
      data.qrCode = `data:image/png;base64,${qr}`;
    }
  }

  return data;
}

// ─── 4. POST /api/auth/2fa/confirm ───────────────────────────────────────────
// Response: { status: "authenticated", accessToken, refreshToken, user }
export async function authTotpConfirm(setupToken, code) {
  const data = await post("/wp-json/threecx/v1/auth/2fa/confirm", {
    setupToken,
    code,
  });

  if (data?.accessToken) saveAuthValue(AUTH_STORAGE_KEYS.accessToken, data.accessToken);
  if (data?.refreshToken) saveAuthValue(AUTH_STORAGE_KEYS.refreshToken, data.refreshToken);
  if (data?.accessToken || data?.refreshToken) {
    saveAuthValue(AUTH_STORAGE_KEYS.hasSession, "1");
    const partnerId = data.user?.partnerId || getAuthValue(AUTH_STORAGE_KEYS.partnerId);
    if (partnerId) saveAuthValue(AUTH_STORAGE_KEYS.partnerId, partnerId);
  }

  return data;
}

// ─── 5. POST /api/auth/2fa/verify ────────────────────────────────────────────
// Response: { status: "authenticated", accessToken, refreshToken, user }
export async function authTotpVerify(tempToken, code) {
  const data = await post("/wp-json/threecx/v1/auth/2fa/verify", { tempToken, code });

  if (data?.accessToken) saveAuthValue(AUTH_STORAGE_KEYS.accessToken, data.accessToken);
  if (data?.refreshToken) saveAuthValue(AUTH_STORAGE_KEYS.refreshToken, data.refreshToken);
  if (data?.accessToken || data?.refreshToken) {
    saveAuthValue(AUTH_STORAGE_KEYS.hasSession, "1");
    const partnerId = data.user?.partnerId || getAuthValue(AUTH_STORAGE_KEYS.partnerId);
    if (partnerId) saveAuthValue(AUTH_STORAGE_KEYS.partnerId, partnerId);
  }

  return data;
}

// ─── WP session bridge ───────────────────────────────────────────────────────
// Temporary bridge to establish WordPress cookie session.
export async function establishWordPressSession() {
  const apiKey = process.env.REACT_APP_WP_SESSION_API_KEY;
  if (!apiKey) return;
  await post("/wp-json/threecx/v1/login", { apiKey });
}